import { css } from "@emotion/css";
import { Checkbox, ExternalLink, PopoverBasicHelp, RadioButtonNew, RadioGroupNew } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDSyncProperties } from "@octopusdeploy/legacy-action-properties";
import { ArgoCDSyncModes } from "@octopusdeploy/legacy-action-properties";
import { type EnvironmentV2Resource, type ProcessType, type PersistenceSettings, type ProcessTemplateParameterResource, unbindParameterName } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import { isProcessOrProjectTemplateProcessType } from "~/areas/projects/components/Process/types";
import { ArgoCDEnvironmentMultiSelect } from "~/components/Actions/argocd/ArgoCDEnvironmentMultiSelect";
import { ExpandableFormSection, Summary } from "~/components/form/index";
const styles = {
    contentPanelContainer: css({
        paddingLeft: space[12],
    }),
    specificEnvironmentsContainer: css({
        marginTop: space[12],
    }),
};
const ContentPanel = (props: ArgoCDTriggerSyncFormSectionProps) => {
    const { properties, setProperties, environments, processType, persistenceSettings } = props;
    const setSyncMode = (value: string | boolean) => {
        if (typeof value === "boolean") {
            value = value ? ArgoCDSyncModes.AllEnvironments : ArgoCDSyncModes.Disabled;
        }
        let updateObject: Partial<ArgoCDSyncProperties> = { "Octopus.Action.ArgoCD.Sync.Mode": value };
        if (value === ArgoCDSyncModes.AllEnvironments) {
            updateObject = {
                ...updateObject,
                "Octopus.Action.ArgoCD.Sync.Environments": undefined,
                "Octopus.Action.ArgoCD.Sync.EnvironmentsTemplateParameter": undefined,
            };
        }
        setProperties(updateObject);
    };
    const selectedEnvIds = useMemo(() => {
        const value = properties["Octopus.Action.ArgoCD.Sync.Environments"];
        if (StringHelper.isNullOrWhiteSpace(value)) {
            return [];
        }
        return value.split(",");
    }, [properties]);
    const onSelectedEnvIdsChanged = (newValue: string[]) => {
        setProperties({ "Octopus.Action.ArgoCD.Sync.Environments": newValue.join(",") });
    };
    const onTemplateParameterChanged = (newValue: string | null) => {
        setProperties({ "Octopus.Action.ArgoCD.Sync.EnvironmentsTemplateParameter": newValue ?? undefined });
    };
    const mode = properties["Octopus.Action.ArgoCD.Sync.Mode"];
    return (<RadioGroupNew label="" value={mode} onChange={setSyncMode}>
            <RadioButtonNew name={"ArgoCDSyncModes"} value={ArgoCDSyncModes.AllEnvironments} label={"All environments"}/>
            <RadioButtonNew name={"ArgoCDSyncModes"} value={ArgoCDSyncModes.PerEnvironment} label={"Specific environments"}/>
            {mode === ArgoCDSyncModes.PerEnvironment && (<div className={styles.specificEnvironmentsContainer}>
                    <ArgoCDEnvironmentMultiSelect processType={processType} persistenceSettings={persistenceSettings} environments={environments} selectedEnvironmentIds={selectedEnvIds} onSelectedEnvironmentIdsChanged={onSelectedEnvIdsChanged} templateParameter={properties["Octopus.Action.ArgoCD.Sync.EnvironmentsTemplateParameter"]} onTemplateParameterChanged={onTemplateParameterChanged}/>
                </div>)}
        </RadioGroupNew>);
};
type ArgoCDTriggerSyncFormSectionProps = {
    expandedByDefault?: boolean;
    environments: EnvironmentV2Resource[] | undefined;
    processType: ProcessType | undefined;
    persistenceSettings: PersistenceSettings | undefined;
    properties: ArgoCDSyncProperties;
    setProperties: (properties: Partial<ArgoCDSyncProperties>, initialize?: boolean, callback?: () => void) => void;
    onParameterAdded?: (parameters: ProcessTemplateParameterResource[]) => void;
};
export const ArgoCDTriggerSyncFormSection = (props: ArgoCDTriggerSyncFormSectionProps) => {
    const { expandedByDefault, properties, setProperties, processType, ...rest } = props;
    const mode = properties["Octopus.Action.ArgoCD.Sync.Mode"];
    const envs = properties["Octopus.Action.ArgoCD.Sync.Environments"];
    const environmentsTemplateParameter = properties["Octopus.Action.ArgoCD.Sync.EnvironmentsTemplateParameter"];
    const summary = useMemo(() => {
        switch (mode) {
            case ArgoCDSyncModes.AllEnvironments:
                return Summary.default("Sync on for all environments");
            case ArgoCDSyncModes.PerEnvironment:
                if (isProcessOrProjectTemplateProcessType(processType)) {
                    return Summary.summary(<span>
                            Create pull requests for environments specified by the parameter <strong>{unbindParameterName(environmentsTemplateParameter)}</strong>
                        </span>);
                }
                else {
                    const envCount = (envs || "").split(",").filter((x) => !StringHelper.isNullOrWhiteSpace(x)).length;
                    return Summary.summary(`Sync on for ${envCount} environment${envCount === 1 ? "" : "s"}`);
                }
            case ArgoCDSyncModes.Disabled:
            default:
                return Summary.summary("Sync off");
        }
    }, [envs, mode, processType, environmentsTemplateParameter]);
    const setSyncMode = (enabled: boolean) => {
        let updateObject: Partial<ArgoCDSyncProperties> = { "Octopus.Action.ArgoCD.Sync.Mode": enabled ? ArgoCDSyncModes.AllEnvironments : ArgoCDSyncModes.Disabled };
        if (!enabled) {
            updateObject = {
                ...updateObject,
                "Octopus.Action.ArgoCD.Sync.Environments": undefined,
                "Octopus.Action.ArgoCD.Sync.EnvironmentsTemplateParameter": undefined,
            };
        }
        setProperties(updateObject);
    };
    const isSyncEnabled = !StringHelper.isNullOrWhiteSpace(mode) && mode !== ArgoCDSyncModes.Disabled;
    return (<ExpandableFormSection isExpandedByDefault={expandedByDefault} errorKey={"Octopus.Action.ArgoCD.Sync.Mode|Octopus.Action.ArgoCD.Sync.Environments"} title={"Trigger Sync"} summary={summary} help="Trigger an Argo CD sync after an update is made" contextualHelp={<PopoverBasicHelp description={<>
                            <p>Octopus will trigger a sync during the deployment.</p>
                            <p>It will also apply the sync policy configured by the user if available.</p>
                            <p>
                                <ExternalLink href={"argocd-sync-policy-docs"}>Sync Policy</ExternalLink>
                            </p>
                        </>} placement={"right-start"}/>}>
            <Checkbox value={isSyncEnabled} onChange={setSyncMode} label={"Sync Argo CD"}/>
            {isSyncEnabled && (<div className={styles.contentPanelContainer}>
                    <ContentPanel properties={properties} setProperties={setProperties} processType={processType} {...rest}/>
                </div>)}
        </ExpandableFormSection>);
};
